/* eslint-disable import/prefer-default-export */
import { createAsyncThunk } from '@reduxjs/toolkit';
import UserService from '@/services/UserService';
import BranchesAndTeamsService from '@/services/BranchesAndTeamsService';
import getDrfErrorMessage from '@/components/BranchesAndTeams/drfErrorMessage';

const handler = (name, asyncFunction) =>
  createAsyncThunk(`UserProfile/${name}`, async (arg) => {
    const data = await asyncFunction(arg);
    return data;
  });

const handlerWithStatus = (name, asyncFunction) =>
  createAsyncThunk(`UserProfile/${name}`, async (arg, { rejectWithValue }) => {
    try {
      return await asyncFunction(arg);
    } catch (error) {
      return rejectWithValue(error.status ?? null);
    }
  });

export const getProfile = handler('getProfile', UserService.getUserProfile);
export const refreshProfile = handler(
  'refreshProfile',
  UserService.getUserProfile
);
export const getHistoryFilters = handlerWithStatus(
  'getHistoryFilters',
  UserService.getUserHistoryFilters
);
export const getHistory = handlerWithStatus(
  'getHistory',
  UserService.getUserHistory
);

// PATCHes (and picture uploads) go out one at a time: each response is the full profile, so two
// saves in flight could resolve out of order and the older one would overwrite
// the newer change in the store.
let pendingProfileUpdate = Promise.resolve();

export const updateProfile = createAsyncThunk(
  'UserProfile/updateProfile',
  async (arg, { rejectWithValue }) => {
    const request = pendingProfileUpdate.then(() =>
      UserService.updateUserProfile(arg)
    );
    pendingProfileUpdate = request.catch(() => {});

    try {
      return await request;
    } catch (error) {
      return rejectWithValue(
        error.status ? getDrfErrorMessage(error.response) : null
      );
    }
  }
);

export const uploadProfilePicture = createAsyncThunk(
  'UserProfile/uploadProfilePicture',
  async ({ file }, { rejectWithValue }) => {
    // Shares the PATCH queue: a save that resolves after the upload would
    // otherwise overwrite `picture` with the URL from before it.
    const request = pendingProfileUpdate.then(() =>
      UserService.uploadProfilePicture(file)
    );
    pendingProfileUpdate = request.catch(() => {});

    try {
      return await request;
    } catch (error) {
      // Serializer errors come as { errors: { file: [msg] } }, the rest as
      // { error: msg } (already in error.message).
      const fileError = error.response?.errors?.file?.[0];
      return rejectWithValue(error.status ? fileError || error.message : null);
    }
  }
);

export const deleteProfilePicture = createAsyncThunk(
  'UserProfile/deleteProfilePicture',
  async (_, { rejectWithValue }) => {
    // Shares the PATCH queue for the same reason as the upload.
    const request = pendingProfileUpdate.then(() =>
      UserService.deleteProfilePicture()
    );
    pendingProfileUpdate = request.catch(() => {});

    try {
      return await request;
    } catch (error) {
      return rejectWithValue(error.status ?? null);
    }
  }
);

// Saves of a user go out one at a time: the store rolls a failed save back to
// the last value the backend confirmed for that key (`saves[key].confirmed`),
// which only holds if saves settle in the order they were made. Queues are
// per user so a stuck save never holds up another user's panel.
const pendingPermissionsUpdates = new Map();

const queuePermissionsRequest = (userId, makeRequest) => {
  const key = String(userId);
  const request = (
    pendingPermissionsUpdates.get(key) ?? Promise.resolve()
  ).then(makeRequest);
  pendingPermissionsUpdates.set(
    key,
    request.catch(() => {})
  );
  return request;
};

const waitForPermissionsSaves = (userId) =>
  pendingPermissionsUpdates.get(String(userId));

// The GET waits for the saves in flight, otherwise it could return the panel
// from before a write the backend is about to apply.
export const getUserPermissions = handlerWithStatus(
  'getUserPermissions',
  async (userId) => {
    await waitForPermissionsSaves(userId);
    return UserService.getUserPermissions(userId);
  }
);

// Reloads the panel without clearing it, to resync after a failed save.
export const refreshUserPermissions = handlerWithStatus(
  'refreshUserPermissions',
  async (userId) => {
    await waitForPermissionsSaves(userId);
    return UserService.getUserPermissions(userId);
  }
);

export const updateUserPermissions = createAsyncThunk(
  'UserProfile/updateUserPermissions',
  async ({ userId, perms, groups, isCompanyAdmin }, { rejectWithValue }) => {
    const changes = {
      ...(isCompanyAdmin !== undefined && { is_company_admin: isCompanyAdmin }),
      ...(perms && { perms }),
      ...(groups && {
        groups: Object.entries(groups).map(([id, assigned]) => ({
          id: Number(id),
          assigned,
        })),
      }),
    };
    const request = queuePermissionsRequest(userId, () =>
      UserService.updateUserPermissions({ userId, changes })
    );

    try {
      return await request;
    } catch (error) {
      return rejectWithValue(error.status ?? null);
    }
  }
);

export const setUserActive = createAsyncThunk(
  'UserProfile/setUserActive',
  async (arg, { rejectWithValue }) => {
    // Shares the PATCH queue: a save that resolves after it would otherwise
    // overwrite `is_active` with the value from before.
    const request = pendingProfileUpdate.then(() =>
      UserService.setUserActive(arg)
    );
    pendingProfileUpdate = request.catch(() => {});

    try {
      return await request;
    } catch (error) {
      // The view's own 403s come as { error }; DRF's (expired session, CSRF)
      // come as { detail } and must not read as owner/plan limit.
      if (error.status === 403 && !error.response?.error) {
        return rejectWithValue(null);
      }
      return rejectWithValue(error.status ?? null);
    }
  }
);

// Only offered for inactive agents: the properties endpoint rejects an agent
// that is still active.
// Each transfer is enqueued on its own, so one can start while the other
// fails: the started ones come back as tasks to follow.
export const transferAgentRecords = createAsyncThunk(
  'UserProfile/transferAgentRecords',
  async ({ oldAgentId, propertiesAgentId, contactsAgentId }) => {
    const requested = [
      propertiesAgentId && {
        resource: 'properties',
        request: UserService.transferAgentProperties({
          oldAgentId,
          newAgentId: propertiesAgentId,
        }),
      },
      contactsAgentId && {
        resource: 'contacts',
        request: UserService.transferAgentContacts({
          oldAgentId,
          newAgentId: contactsAgentId,
        }),
      },
    ].filter(Boolean);
    const results = await Promise.allSettled(
      requested.map(({ request }) => request)
    );
    return {
      tasks: requested.flatMap(({ resource }, index) =>
        results[index].status === 'fulfilled'
          ? [{ resource, taskId: results[index].value.task_id }]
          : []
      ),
      hasFailures: results.some(({ status }) => status === 'rejected'),
    };
  }
);

export const AGENT_TRANSFER_POLL_MS = 3000;
const MAX_TRANSFER_POLL_FAILURES = 5;
// ~10 minutes at AGENT_TRANSFER_POLL_MS, so a job stuck in `pending` ends.
const MAX_TRANSFER_POLL_ATTEMPTS = 200;

const wait = (ms) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

// How long the tab has to stay visible before polling resumes, so flicking
// through tabs doesn't fire a request on every pass.
export const VISIBLE_TAB_SETTLE_MS = 1000;

// Can't use `useVisibility` here: the thunk outlives the component.
const waitUntilVisible = () =>
  new Promise((resolve) => {
    if (!document.hidden) {
      resolve();
      return;
    }
    let settleTimer;
    const handleVisibilityChange = () => {
      clearTimeout(settleTimer);
      if (document.hidden) return;
      settleTimer = setTimeout(() => {
        document.removeEventListener(
          'visibilitychange',
          handleVisibilityChange
        );
        resolve();
      }, VISIBLE_TAB_SETTLE_MS);
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
  });

// Resolves with `done` or `rejected`, or null once the job can't be followed
// anymore (no longer tracked, stuck in `pending`, or the status keeps failing).
const pollAgentTransfer = async (taskId, failures = 0, attempt = 1) => {
  await wait(AGENT_TRANSFER_POLL_MS);
  await waitUntilVisible();
  try {
    const { status } = await UserService.getAgentTransferStatus(taskId);
    if (status !== 'pending') return status;
    return attempt >= MAX_TRANSFER_POLL_ATTEMPTS
      ? null
      : pollAgentTransfer(taskId, 0, attempt + 1);
  } catch (error) {
    if (error.status === 404 || failures + 1 >= MAX_TRANSFER_POLL_FAILURES) {
      return null;
    }
    return pollAgentTransfer(taskId, failures + 1, attempt + 1);
  }
};

// Lives in a thunk so it keeps polling after the profile unmounts.
export const watchAgentTransfers = createAsyncThunk(
  'UserProfile/watchAgentTransfers',
  async (tasks) => {
    const statuses = await Promise.all(
      tasks.map(({ taskId }) => pollAgentTransfer(taskId))
    );
    return tasks.map((task, index) => ({ ...task, status: statuses[index] }));
  }
);

// The branches list is admin only: a 403 there would log a non admin out.
export const getEditOptions = createAsyncThunk(
  'UserProfile/getEditOptions',
  async ({ withBranches }, { rejectWithValue }) => {
    const [branches, teams] = await Promise.allSettled([
      withBranches ? BranchesAndTeamsService.listBranches() : null,
      BranchesAndTeamsService.listTeams(),
    ]);
    const resultsOf = ({ status, value }) =>
      status === 'fulfilled' && value ? value.results : null;

    if (branches.status === 'rejected' && teams.status === 'rejected') {
      return rejectWithValue(null);
    }
    return { branches: resultsOf(branches), teams: resultsOf(teams) };
  }
);
